import api from 'api'
import { UIUserPermission } from 'api/admin/userPermissions/permissions'
import {
  CreateUserPermissionsRequest,
  GrantUserPermissionRequest,
  GrantUserPermissionResponse,
} from 'api/admin/userPermissions/types'

function dateTimeToAwareISO(
  dateTimeStr: string | null | undefined
): string | null {
  if (!dateTimeStr) {
    return null
  }
  const parsed = new Date(dateTimeStr)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed.toISOString()
}

function effectiveAtToApiValue(
  dateTimeStr: string | null | undefined
): string | null {
  if (dateTimeStr === null || dateTimeStr === undefined || dateTimeStr === '') {
    return null
  }
  const parsed = new Date(dateTimeStr)
  if (Number.isNaN(parsed.getTime()) || parsed <= new Date()) {
    // Backend defaults null effectiveAt to now; past/now would be rejected.
    return null
  }
  return dateTimeToAwareISO(dateTimeStr)
}

function getRoute(permission: UIUserPermission): string {
  switch (permission) {
    case 'permission_admin':
      return '/admin/permissions/grant/permission_admin'
    case 'support_admin':
      return '/admin/permissions/grant/support_admin'
    case 'support_specialist':
      return '/admin/permissions/grant/support_specialist'
    case 'finance_admin':
      return '/admin/permissions/grant/finance_admin'
    case 'company_scope_manager':
      return '/admin/permissions/grant/company_scope_manager'
    default:
      permission satisfies never
      throw new Error(`Unhandled permission: ${permission}`)
  }
}

async function grantUserPermission(
  request: GrantUserPermissionRequest,
  permission: UIUserPermission
): Promise<GrantUserPermissionResponse> {
  const route = getRoute(permission)
  const result = await api.post<GrantUserPermissionResponse>(route, {
    userId: request.userId,
    permissions: request.permissions.map((grant) => ({
      permission: grant.permission,
      effectiveAt: effectiveAtToApiValue(grant.effectiveAt),
      expiresAt: dateTimeToAwareISO(grant.expiresAt),
    })),
    companyScopes: request.companyScopes.map((grant) => ({
      companyId: grant.companyId,
      companyGroupId: grant.companyGroupId,
      allCompanies: grant.allCompanies,
      effectiveAt: effectiveAtToApiValue(grant.effectiveAt),
      expiresAt: dateTimeToAwareISO(grant.expiresAt),
    })),
  })
  return result.data
}

export async function createUserPermissions(
  request: CreateUserPermissionsRequest,
  permission: UIUserPermission
): Promise<GrantUserPermissionResponse> {
  const granted = await grantUserPermission(
    {
      userId: request.userId,
      permissions: request.permissions,
      companyScopes: request.companyScopes,
    },
    permission
  )
  return granted
}
